/**
 * Read-only list of a document's or packet's signature fields. All editing
 * happens in the placement editor; clicking a row opens it on that field.
 *
 * Copied from corp-dashboard/components/esign/fields-summary.tsx.
 */

import type { EsignField } from '@/services/documentLibrary.service';
import { fieldTypeLabel } from '@/services/documentLibrary.service';
import { describePlacement, fieldColour } from '@/lib/esign/fields';

export interface FieldsSummaryProps {
  fields: EsignField[];
  /** A row was clicked: open the editor on this field. */
  onSelect: (fieldId: number) => void;
}

export function FieldsSummary({ fields, onSelect }: FieldsSummaryProps) {
  if (fields.length === 0) {
    return <p className="text-sm text-gray-500">No fields yet.</p>;
  }

  return (
    <ul className="space-y-1">
      {fields.map((field, index) => {
        const placement = describePlacement(field.tabKeywords ?? '', field.signaturePageNum ?? '');
        const title = field.title.trim() || 'Field';
        return (
          <li key={field.documentKeywordID}>
            <button
              type="button"
              onClick={() => onSelect(field.documentKeywordID)}
              className="flex w-full items-baseline gap-3 rounded border border-l-4 bg-white px-3 py-1.5 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              style={{ borderLeftColor: fieldColour(index) }}
              data-testid="esign-field-row"
            >
              <span className="font-medium text-gray-900">{title}</span>
              <span className="text-gray-500">{fieldTypeLabel(field.keywordType)}</span>
              {placement.kind === 'words' && <span className="truncate text-gray-700">&ldquo;{placement.words}&rdquo;</span>}
              {placement.kind === 'fixed' && <span className="text-gray-700">Fixed spot on page {placement.page}</span>}
              {placement.kind === 'missing' && <span className="font-medium text-amber-700">No words yet</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
